import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import RouteImg from '../assets/images/learning-route/route.png';
import BadgeImg from '../assets/images/learning-route/badge.png';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { Trans, useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { LoadLearningRouteCertificates } from '../utils/queries.js';
import { Arrow } from './Faqs.jsx';
import { OpenLinkIcon } from './icons/OpenLinkIcon.jsx';
import { certificateDetails } from '../interact.jsx';
import { border } from '../utils/theme.js';
import { useEffect, useState } from 'react';
import { encryptedCertificateImageUrl } from '../interact.jsx';

export default function LearningRoutesContainer({ certificate }) {
    const { t } = useTranslation();
    const [achievedCredentialAccepted, setAchievedCredentialAccepted] = useState(false);
    const data = certificate.learningRoutes?.length
        ? LoadLearningRouteCertificates(certificate.id, certificate.learningRoutes[0].id)?.data
        : undefined;

    useEffect(() => {
        if (data?.achieved) {
            certificateDetails(data.achieved.id)
                .then(() => setAchievedCredentialAccepted(true))
                .catch((e) => (e.response?.status === 404 ? undefined : Promise.reject(e)));
        }
    }, [data]);
    return data ? (
        <>
            <Accordion
                aria-controls="lr-content"
                id="lr-header"
                sx={{
                    ...border,
                    borderRadius: 4,
                    paddingY: 2,
                    maxHeight: '500px',
                    overflow: 'auto',
                    '&:before': {
                        display: 'none',
                    },
                }}
            >
                <AccordionSummary expandIcon={<Arrow />}>
                    <Grid container spacing={1} sx={{ width: '100%', alignItems: '' }}>
                        <Grid size={3}>
                            <img src={RouteImg} alt="route" width={48} />
                        </Grid>
                        <Grid size={9}>
                            <Stack>
                                <Typography variant="subtitle1">{t('learning_routes.title')}</Typography>
                                <Typography variant="body2">
                                    {data.achieved ? (
                                        <Trans
                                            i18nKey={'learning_routes.subtitle'}
                                            values={{ count: data.steps.length }}
                                        />
                                    ) : (
                                        <Trans
                                            i18nKey={'learning_routes.subtitle_incomplete'}
                                            values={{
                                                count: data.steps.filter((x) => x.certificate).length,
                                                total: data.steps.length,
                                            }}
                                        />
                                    )}
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack>
                        {data.steps.map((item, index) => (
                            <LearningRoute
                                key={item.name + index}
                                certificate={item.certificate}
                                currentCertificate={certificate}
                                name={item.name}
                                margin={index === data.steps.length - 1 ? 0 : 3}
                            />
                        ))}
                        {data.achieved && (
                            <>
                                <Typography variant="subtitle1" my={2}>
                                    {t('learning_routes.final')}
                                </Typography>
                                <LearningRoute
                                    certificate={data.achieved}
                                    currentCertificate={certificate}
                                    name={t('learning_routes.final')}
                                    margin={1}
                                    isAccepted={achievedCredentialAccepted}
                                />
                            </>
                        )}
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </>
    ) : (
        <></>
    );
}

function applyDateFormat(date, dateFormat) {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear());
    if (dateFormat?.toUpperCase() === 'MM/DD/YYYY'.toUpperCase()) return `${month}/${day}/${year}`;
    return `${day}/${month}/${year}`;
}

const LearningRoute = ({ certificate, name, margin, currentCertificate, isAccepted = true }) => {
    const disabled = '#CCC9D1';
    return (
        <Grid
            container
            spacing={1}
            key={name}
            sx={{
                width: '100%',
                paddingBottom: 3,
                alignItems: 'initial',
                paddingTop: 1,
                paddingX: 2,
                background: '#F6F6F6',
                borderRadius: 3,
                marginBottom: margin,
            }}
        >
            <Grid size={{ xs: 4, md: 3 }}>
                <img
                    src={certificate ? encryptedCertificateImageUrl(certificate.id) : BadgeImg}
                    alt="badge"
                    height={40}
                    style={{ borderRadius: 2 }}
                />
            </Grid>
            <Grid size={{ md: 7, xs: 6 }}>
                <Stack>
                    <Typography
                        sx={{
                            display: '-webkit-box',
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 3,
                            fontWeight: 600,
                            color: certificate ? '#1E1E1E' : disabled,
                        }}
                        variant="body2"
                    >
                        {name}
                    </Typography>
                    <Typography variant={'caption'} sx={{ color: certificate ? '#1E1E1E' : disabled }}>
                        {certificate ? (
                            <Trans
                                i18nKey={'learning_routes.obtained'}
                                tOptions={{ interpolation: { escapeValue: false } }}
                                values={{ date: applyDateFormat(certificate.createdAt, certificate.dateFormat) }}
                            />
                        ) : (
                            <Trans i18nKey={'learning_routes.not_obtained'} />
                        )}
                    </Typography>
                </Stack>
            </Grid>
            {isAccepted && (
                <Grid
                    size={{ md: 2, xs: 2 }}
                    sx={{ cursor: certificate ? 'pointer' : 'not-allowed', textAlign: 'end' }}
                >
                    {currentCertificate.id !== certificate?.id && (
                        <a href={certificate?.url} target="_blank" rel="noopener noreferrer">
                            <OpenLinkIcon fill={certificate ? '#548CFF' : disabled} />
                        </a>
                    )}
                </Grid>
            )}
        </Grid>
    );
};
